import React, { FC, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { CHROME_WEB_STORE_URL, getExtensionInfo } from "../../services/extension";
import LinkedInCards from "../Outreach/LinkedInCards";
import LinkedInConversations, { ConversationPerson } from "../Outreach/LinkedInConversations";

/**
 * The iframe modal the boring HubSpot sidebar card opens
 * (`/hubspot/linkedin?contactId=&portalId=&mode=messages|profile`).
 *
 * It has no app chrome. LinkedIn is read with the rep's own browser session,
 * through the boring extension, the same way as the Outreach tiles:
 * mode=profile is the profile and activity cards, mode=messages is the
 * conversation tile with its composer (replies are queued and sent from the
 * rep's LinkedIn session). The HubSpot card passes the contact's LinkedIn URL
 * and name, since neither the extension nor this page can look a HubSpot
 * contact up by id.
 */

const INK = "#33475b"; // HubSpot's body text, so the modal reads as part of the record
const LINK = "#0091ae";

const Message = styled("div")`
  font-family: "Lexend Deca", "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: ${INK};
  padding: 32px;
  max-width: 520px;

  a {
    color: ${LINK};
  }
`;

type Mode = "messages" | "profile";

const readParams = () => {
  const params = new URLSearchParams(window.location.search);
  const contactId = params.get("contactId") || "";
  const mode = params.get("mode");
  const linkedInUrl = params.get("linkedInUrl") || "";
  return {
    contactId: /^\d+$/.test(contactId) ? contactId : "",
    portalId: params.get("portalId") || "",
    mode: (mode === "messages" || mode === "profile" ? mode : null) as Mode | null,
    // Only a LinkedIn profile URL is passed on to the extension.
    linkedInUrl: /^https:\/\/([a-z]+\.)?linkedin\.com\/in\/[^/?#\s]+/i.test(linkedInUrl) ? linkedInUrl : "",
    name: (params.get("name") || "").slice(0, 120),
  };
};

const HubSpotLinkedIn: FC = () => {
  const { contactId, portalId, mode, linkedInUrl, name } = useMemo(readParams, []);
  const [extension, setExtension] = useState<"checking" | "installed" | "missing">("checking");

  useEffect(() => {
    getExtensionInfo().then((info) => setExtension(info.installed ? "installed" : "missing"));
  }, []);

  // Stable identities, so the reused Outreach views do not reload on every render.
  const hubspot = useMemo(() => ({ contactId, linkedInUrl }), [contactId, linkedInUrl]);
  const person = useMemo<ConversationPerson>(
    () => ({ id: contactId, linkedinUrl: linkedInUrl || undefined, name: name || undefined, cacheScope: `hubspot-${portalId}` }),
    [contactId, linkedInUrl, name, portalId]
  );

  if (!contactId || !mode) return <Message>{"Open this from a contact's LinkedIn card in HubSpot."}</Message>;

  if (extension === "checking") return null;
  if (extension === "missing") {
    return (
      <Message>
        <p>LinkedIn conversations and activity are read with your own LinkedIn session, through the boring Chrome extension.</p>
        <p>
          <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
            Install boring from the Chrome Web Store
          </a>
          , sign in to it, then open this again.
        </p>
      </Message>
    );
  }

  if (!linkedInUrl && mode === "profile") return <Message>This contact has no LinkedIn profile in HubSpot.</Message>;

  return mode === "messages" ? <LinkedInConversations variant="tile" person={person} /> : <LinkedInCards variant="full" hubspot={hubspot} />;
};

export default HubSpotLinkedIn;
